// Team CRUD interface. Mirrors api.js — reads teams/manifest.json in
// demo mode; future Supabase swap lands here without touching pages.

import { isConfigured } from './config.js';
import { loadTeam } from '../../shared/team-schema.js';

const TEAMS_DIR = globalThis.__RM_TEAMS_BASE__ ?? '../teams/';
const MANIFEST_PATH = `${TEAMS_DIR}manifest.json`;
let manifestCache = null;

export async function listTeams() {
  if (isConfigured()) throw new Error('Supabase client not implemented yet.');
  const manifest = await loadManifest();
  const summaries = await Promise.all(
    manifest.teams.map(async filename => {
      try {
        const id = idFromFilename(filename);
        const data = await fetchTeamFile(filename);
        return summarize(id, data);
      } catch (err) {
        console.error(`Failed to load team "${filename}":`, err);
        return null;
      }
    }),
  );
  return summaries.filter(Boolean);
}

export async function getTeam(id) {
  if (isConfigured()) throw new Error('Supabase client not implemented yet.');
  const manifest = await loadManifest();
  const filename = manifest.teams.find(f => idFromFilename(f) === id);
  if (!filename) throw new Error(`Unknown team: ${id}`);
  const data = await fetchTeamFile(filename);
  const { team } = loadTeam(data);
  return team;
}

async function loadManifest() {
  if (manifestCache) return manifestCache;
  // Cache-bust + no-store: without this, the browser's HTTP cache can
  // serve a stale manifest after files were renamed on disk, listing
  // filenames that 404 silently and leaving the list empty.
  const res = await fetch(`${MANIFEST_PATH}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Failed to load teams manifest: ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data?.teams)) throw new Error('Teams manifest is malformed: missing "teams" array');
  manifestCache = data;
  return data;
}

async function fetchTeamFile(filename) {
  const url = `${TEAMS_DIR}${filename}?t=${Date.now()}`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function idFromFilename(filename) {
  return filename.replace(/\.team\.json$/, '').replace(/\.json$/, '');
}

function summarize(id, data) {
  // The listing already loads the full file, so include the bits the
  // race-setup picker needs (entries + livery) instead of forcing a
  // second per-team fetch. Entries are normalized to the loaded
  // shape; missing carNumber lands as null.
  const entries = Array.isArray(data.entries)
    ? data.entries
        .filter(e => e && typeof e.driverId === 'string')
        .map(e => ({ driverId: e.driverId, carNumber: typeof e.carNumber === 'number' ? e.carNumber : null }))
    : [];
  return {
    id,
    name: data.name || id,
    shortName: data.shortName || '',
    primary: data.livery?.primary || '#003366',
    accent: data.livery?.accent || '#ffcc00',
    carId: data.carId || null,
    entries,
    entryCount: entries.length,
  };
}
